import React from "react";
import { Box, Typography, Button, CircularProgress, Chip, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { Inbox } from "lucide-react";

export function PageHeader({ title, subtitle, action }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{title}</Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{subtitle}</Typography>
        )}
      </Box>
      {action}
    </Box>
  );
}

export function Loader({ label = "Loading..." }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8, gap: 2 }}>
      <CircularProgress size={28} sx={{ color: "#000" }} />
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Box>
  );
}

export function EmptyState({ title = "Nothing here yet", subtitle, action }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8, gap: 1.5 }}>
      <Inbox size={36} color="#9E9E9E" strokeWidth={1.5} />
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
      {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
      {action}
    </Box>
  );
}

const STATUS_COLOR_MAP = {
  open: { bg: "#E8E8E8", fg: "#111" },
  pending: { bg: "#FFF3CD", fg: "#8A6D00" },
  resolved: { bg: "#DDEEDD", fg: "#1B5E20" },
  closed: { bg: "#111111", fg: "#FFFFFF" },
  draft: { bg: "#EEE", fg: "#555" },
  pending_review: { bg: "#FFF3CD", fg: "#8A6D00" },
  published: { bg: "#DDEEDD", fg: "#1B5E20" },
  active: { bg: "#DDEEDD", fg: "#1B5E20" },
  assigned: { bg: "#E8E8E8", fg: "#111" },
  in_stock: { bg: "#EEE", fg: "#555" },
  maintenance: { bg: "#FFF3CD", fg: "#8A6D00" },
  retired: { bg: "#111111", fg: "#FFFFFF" },
  success: { bg: "#DDEEDD", fg: "#1B5E20" },
  failed: { bg: "#F8D7DA", fg: "#B00020" },
};

export function StatusChip({ value, label }) {
  const key = (value || "").toLowerCase();
  const colors = STATUS_COLOR_MAP[key] || { bg: "#EEE", fg: "#333" };
  return (
    <Chip
      size="small"
      label={label || value}
      sx={{ backgroundColor: colors.bg, color: colors.fg, fontWeight: 700, textTransform: "capitalize" }}
    />
  );
}

export function ConfirmDialog({ open, title, message, confirmLabel = "Confirm", danger, onConfirm, onCancel, loading }) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">{message}</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onCancel} color="inherit">Cancel</Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={loading}
          sx={danger ? { bgcolor: "#B00020", "&:hover": { bgcolor: "#8c0018" } } : {}}
        >
          {loading ? "Working..." : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
